import { useState, useCallback } from 'react';
import { PAGINATION } from '../constants/config';

/**
 * Default page size constant.
 * @deprecated Use PAGINATION.DEFAULT_PAGE_SIZE from '../constants/config' instead
 */
export const DEFAULT_PAGE_SIZE = PAGINATION.DEFAULT_PAGE_SIZE;

export interface UsePaginationReturn {
  /** Current page (0-based, for API) */
  page: number;
  /** Page size */
  size: number;
  /** Total pages count */
  totalPages: number;
  /** Total elements count */
  totalElements: number;
  /** Set total pages (from API response) */
  setTotalPages: (total: number) => void;
  /** Set total elements (from API response) */
  setTotalElements: (total: number) => void;
  /** Go to specific page (0-based) */
  goToPage: (page: number) => void;
  /** Go to next page */
  nextPage: () => void;
  /** Go to previous page */
  prevPage: () => void;
  /** Reset to first page */
  reset: () => void;
  /** Change page size */
  setSize: (newSize: number) => void;

  // MUI Pagination helpers (1-based)
  /** Current page for MUI (1-based) */
  muiPage: number;
  /** Handler for MUI Pagination onChange */
  handleMuiChange: (event: React.ChangeEvent<unknown>, value: number) => void;

  /** Has next page */
  hasNextPage: boolean;
  /** Has previous page */
  hasPrevPage: boolean;
}

/**
 * Hook for managing pagination state.
 * Handles the difference between 0-based (API) and 1-based (MUI) page indices.
 *
 * @param defaultSize - Default page size (default: 20)
 *
 * @example
 * const {
 *   page, size, totalPages, setTotalPages,
 *   muiPage, handleMuiChange, reset
 * } = usePagination(20);
 *
 * // In API call:
 * const response = await api.getItems({ page, size });
 * setTotalPages(response.totalPages);
 *
 * // In component:
 * <Pagination
 *   count={totalPages}
 *   page={muiPage}
 *   onChange={handleMuiChange}
 * />
 */
export const usePagination = (defaultSize: number = DEFAULT_PAGE_SIZE): UsePaginationReturn => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(defaultSize);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const goToPage = useCallback((newPage: number) => {
    setPage(Math.max(0, newPage));
  }, []);

  const nextPage = useCallback(() => {
    setPage(prev => Math.min(prev + 1, totalPages - 1));
  }, [totalPages]);

  const prevPage = useCallback(() => {
    setPage(prev => Math.max(0, prev - 1));
  }, []);

  const reset = useCallback(() => {
    setPage(0);
  }, []);

  const handleSizeChange = useCallback((newSize: number) => {
    setSize(newSize);
    setPage(0); // Reset to first page when size changes
  }, []);

  // MUI Pagination uses 1-based indexing
  const muiPage = page + 1;

  const handleMuiChange = useCallback((_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1);
  }, []);

  return {
    page,
    size,
    totalPages,
    totalElements,
    setTotalPages,
    setTotalElements,
    goToPage,
    nextPage,
    prevPage,
    reset,
    setSize: handleSizeChange,
    muiPage,
    handleMuiChange,
    hasNextPage: page < totalPages - 1,
    hasPrevPage: page > 0,
  };
};

export default usePagination;
